import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonList,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonBadge,
  IonToggle,
  IonRange,
  IonNote
} from '@ionic/react';
import {
  refreshOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
  settingsOutline,
  timeOutline,
  cloudDownloadOutline
} from 'ionicons/icons';
import { Storage } from '@capacitor/storage';
import { checkVersionUpdate, checkAndUpdateData } from '../data/dataApi';
import { versionCheckService } from '../services/VersionCheckService';
import { useVersionNotification } from '../components/VersionUpdateNotification';

const VersionTest: React.FC = () => {
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [serverVersion, setServerVersion] = useState<string>('');
  const [eventCode, setEventCode] = useState<string>('');
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastCheck, setLastCheck] = useState<string>('');
  const [serviceStatus, setServiceStatus] = useState<any>({});
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(true);
  const [checkInterval, setCheckInterval] = useState(15);

  const { hasUpdate, newVersion, checkNow, updateNow, dismissUpdate } = useVersionNotification();

  useEffect(() => {
    loadInitialData();
    loadServiceStatus();
  }, []);

  const loadInitialData = async () => {
    try {
      const [versionStorage, eventStorage] = await Promise.all([
        Storage.get({ key: 'scheduleVersion' }),
        Storage.get({ key: 'eventcode' })
      ]);

      setCurrentVersion(versionStorage.value || '0');
      setEventCode(eventStorage.value || 'nada');
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadServiceStatus = () => {
    const status = versionCheckService.getStatus();
    setServiceStatus(status);
    setAutoCheckEnabled(status.isRunning);
    setCheckInterval(status.config.checkIntervalMinutes);
  };

  const handleCheckVersion = async () => {
    if (!eventCode || eventCode === 'nada') {
      alert('No hay código de evento válido');
      return;
    }

    setIsChecking(true);
    try {
      const result = await checkVersionUpdate(eventCode);
      setServerVersion(result.serverVersion);
      setCurrentVersion(result.currentVersion);
      setLastCheck(new Date().toLocaleString());

      if (result.hasUpdate) {
        alert(`Nueva versión disponible: ${result.currentVersion} → ${result.serverVersion}`);
      } else {
        alert('La versión actual está actualizada');
      }
    } catch (error) {
      console.error('Error checking version:', error);
      alert('Error al verificar versión');
    } finally {
      setIsChecking(false);
    }
  };

  const handleUpdateData = async () => {
    if (!eventCode || eventCode === 'nada') {
      alert('No hay código de evento válido');
      return;
    }

    setIsUpdating(true);
    try {
      const updated = await checkAndUpdateData(eventCode, true);
      
      if (updated) {
        alert('Datos actualizados exitosamente');
        await loadInitialData();
      } else {
        alert('No se pudo actualizar o no hay actualizaciones disponibles');
      }
    } catch (error) {
      console.error('Error updating data:', error);
      alert('Error al actualizar datos');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleAutoCheck = async () => {
    try {
      if (autoCheckEnabled) {
        versionCheckService.stopAutoCheck();
        setAutoCheckEnabled(false);
      } else {
        await versionCheckService.startAutoCheck();
        setAutoCheckEnabled(true);
      }
      loadServiceStatus();
    } catch (error) {
      console.error('Error toggling auto check:', error);
    }
  };

  const handleIntervalChange = (value: number) => {
    setCheckInterval(value);
    versionCheckService.configure({
      checkIntervalMinutes: value
    });
    
    // Reiniciar el servicio si está activo
    if (autoCheckEnabled) {
      versionCheckService.stopAutoCheck();
      versionCheckService.startAutoCheck();
    }
    
    loadServiceStatus();
  };

  const doRefresh = async (event: CustomEvent) => {
    await loadInitialData();
    loadServiceStatus();
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Test de Versiones</IonTitle>
        </IonToolbar>
      </IonHeader>
      
      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        {/* Estado Actual */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Estado Actual</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel>
                  <h3>Código de Evento</h3>
                  <p>{eventCode}</p>
                </IonLabel>
              </IonItem>
              
              <IonItem>
                <IonLabel>
                  <h3>Versión Actual</h3>
                  <p>{currentVersion}</p>
                </IonLabel>
                <IonBadge color="primary">{currentVersion}</IonBadge>
              </IonItem>
              
              {serverVersion && (
                <IonItem>
                  <IonLabel>
                    <h3>Versión del Servidor</h3>
                    <p>{serverVersion}</p>
                  </IonLabel>
                  <IonBadge color={serverVersion !== currentVersion ? "warning" : "success"}>
                    {serverVersion}
                  </IonBadge>
                </IonItem>
              )}
              
              {lastCheck && (
                <IonItem>
                  <IonLabel>
                    <h3>Última Verificación</h3>
                    <p>{lastCheck}</p>
                  </IonLabel>
                  <IonIcon icon={timeOutline} slot="end" />
                </IonItem>
              )}
            </IonList>
          </IonCardContent>
        </IonCard>

        {/* Acciones Manuales */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Acciones Manuales</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonButton 
              expand="block" 
              fill="outline" 
              onClick={handleCheckVersion}
              disabled={isChecking || !eventCode || eventCode === 'nada'}
            >
              {isChecking && <IonSpinner name="crescent" />}
              <IonIcon icon={refreshOutline} slot="start" />
              {isChecking ? 'Verificando...' : 'Verificar Versión'}
            </IonButton>
            
            <IonButton 
              expand="block" 
              color="primary"
              onClick={handleUpdateData}
              disabled={isUpdating || !eventCode || eventCode === 'nada'}
              style={{ marginTop: '10px' }}
            >
              {isUpdating && <IonSpinner name="crescent" />}
              <IonIcon icon={cloudDownloadOutline} slot="start" />
              {isUpdating ? 'Actualizando...' : 'Forzar Actualización'}
            </IonButton>
          </IonCardContent>
        </IonCard>

        {/* Configuración del Servicio */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={settingsOutline} style={{ marginRight: '8px' }} />
              Configuración Automática
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonItem>
              <IonLabel>
                <h3>Verificación Automática</h3>
                <p>Verificar versión periódicamente</p>
              </IonLabel>
              <IonToggle 
                checked={autoCheckEnabled}
                onIonChange={handleToggleAutoCheck}
              />
            </IonItem>
            
            <IonItem>
              <IonLabel>
                <h3>Intervalo de Verificación</h3>
                <p>{checkInterval} minutos</p>
              </IonLabel>
            </IonItem>
            
            <IonRange
              min={5}
              max={60}
              step={5}
              value={checkInterval}
              onIonChange={e => handleIntervalChange(e.detail.value as number)}
              disabled={!autoCheckEnabled}
            >
              <IonLabel slot="start">5m</IonLabel>
              <IonLabel slot="end">60m</IonLabel>
            </IonRange>
            
            <IonNote>
              Estado del servicio: {serviceStatus.isRunning ? 'Activo' : 'Inactivo'}
              {serviceStatus.isChecking && ' (Verificando...)'}
            </IonNote>
          </IonCardContent>
        </IonCard>

        {/* Notificación de Actualización */}
        {hasUpdate && (
          <IonCard color="warning">
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={alertCircleOutline} style={{ marginRight: '8px' }} />
                Actualización Disponible
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>Nueva versión disponible: <strong>v{newVersion}</strong></p>
              </IonText>
              
              <IonButton 
                size="small" 
                color="primary"
                onClick={updateNow}
              >
                Actualizar Ahora
              </IonButton>
              
              <IonButton 
                size="small" 
                fill="clear"
                onClick={dismissUpdate}
              >
                Después
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}
      </IonContent>
    </IonPage>
  );
};

export default VersionTest;
